import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Dropdown } from 'react-bootstrap';
import { Link, NavLink, useHistory, useLocation } from 'react-router-dom';
import { UserContext } from 'contexts/user/UserContext';
import { useMenu } from 'contexts/MenuContextUtils';
import { useSettings } from 'contexts/SettingsContextUtils';
import { localeOptions } from 'constants/defaultValues';
import MappingToolIcon from 'assets/icons/mapping-icons/MappingToolIcon';
import { MenuIcon, MobileMenuIcon } from 'components/svg';
import { gql, useMutation } from '@apollo/client';
import InfoModal from 'components/Modal/InfoModal';
import IntlMessages from 'helpers/IntlMessages';
import { getDirection, setDirection } from 'helpers/Utils';
import { isDev } from 'helpers/helper';
import TranslateIcon from 'mdi-react/TranslateIcon';
import ChatOutlineIcon from 'mdi-react/ChatOutlineIcon';
import AccountGroup from 'mdi-react/AccountGroupIcon';
import CustomDropdownToggle from 'components/custom/CustomDropdownToggle';
import NotificationInbox from 'components/NotificationInbox';
import { useAuth } from 'contexts/AuthContextUtils';

const UPDATE_USER_LANGUAGE = gql`
mutation updateUserLanguage(
  $id: UUID!
  $language: String!
) {
  updateUser(input: { patch: { language: $language }, id: $id }) {
    user {
      username
      language
    }
  }
}
`;

const RESEND_VERIFICATION_EMAIL = gql`
mutation MyMutation($emailId: UUID!) {
  resendEmailVerificationCode(input: {emailId: $emailId}) {
    success
  }
}`


const TopNav = () => {
  const history = useHistory();
  const location = useLocation();
  const { locale, setLocale } = useSettings();
  const showVerifyBanner = new URLSearchParams(location.search).has('unverified');
  const {
    toggleMenu,
  } = useMenu();
  const { isAuthenticated, currentUser, refetchCurrentUser, unreadCount = 0 } = useContext(UserContext);
  const intl = useIntl();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [appLocaleOptions, setAppLocaleOptions] = useState({ id: null, name: locale, direction: null });
  const [verificationEmailResent, setVerificationEmailResent] = useState(false);
  const [verificationEmailResentError, setVerificationEmailResentError] = useState(false);
  const [updateUserLanguage] = useMutation(UPDATE_USER_LANGUAGE);
  const [resendVerificationEmail] = useMutation(RESEND_VERIFICATION_EMAIL);
  const { logout } = useAuth();
  const discourseUrl = import.meta.env.VITE_DISCOURSE_URL || '';
  const chatUrl = discourseUrl ? `${discourseUrl}/chat` : '';

  const handleChangeLocale = () => setShowInfoModal(true);

  const handleLogout = async () => {
    try {
      await logout().then(() => {
        refetchCurrentUser();
      });
      history.push('/logout'); // first go to a non-private page, so that logout doesn't trigger a redirect to login
      window.location.href = isDev ? '/login' : '/page/about';
    } catch (error) {
      return console.error('Error on logging out:', error);
    }
  };

  const handleSettings = () => history.push('/profile/edit');

  const handleProfile = () => history.push('/profile');

  const handleClickLogin = (e) => {
    e.preventDefault();
    history.push({
      pathname: '/login',
      state: {
        from: history.location.pathname,
      }
    });
  };

  const handleAbout = () => {
    window.location.href = '/about';
  };

  const handleContact = () => {
    window.location.href = '/page/contact-us';
  };

  const menuButtonClick = (e) => {
    e.preventDefault();
    toggleMenu();

    setTimeout(() => {
      const event = document.createEvent('HTMLEvents');
      event.initEvent('resize', false, false);
      window.dispatchEvent(event);
    }, 350);
  };

  const mobileMenuButtonClick = (e) => {
    e.preventDefault();
    toggleMenu();
  };

  const handleConfirm = () => {
    setShowInfoModal(false);

    if (appLocaleOptions.id) {

      // for logged in user, store the language in the database
      if (isAuthenticated) {
        currentUser.id && updateUserLanguage({
          variables: {
            id: currentUser.id,
            language: appLocaleOptions.id,
          }
        }).then(() => {
          // only change the app language and save to local storage AFTER the mutation finishes,
          // otherwise the useEffect below would change it back to pre-mutation database value
          setLocale(appLocaleOptions.id);
          // reload the page, so that we are sure that all forms will be loaded/saved with correct language settings
          window.location.reload();
        });
      } else {
        // for non-logged in user
        setLocale(appLocaleOptions.id);
      }
    }

    const currentDirection = getDirection().direction;
    if (appLocaleOptions.direction && appLocaleOptions.direction !== currentDirection) {
      setDirection(appLocaleOptions.direction);
      setTimeout(() => window.location.reload(), 500);
    }
  };

  const ChangeLanguageModalContent = () => {
    const intl = useIntl();

    return (
      <div className="text-center">
        <TranslateIcon
          size={100}
          className="mb-2 accent-color"
        />
        <div className="text-normal mb-4">
          <p>{intl.formatMessage({ id: 'nav.modal-about-change-language' }, { locale: appLocaleOptions.name?.split(' -')[0] })}</p>
          <p><b>{intl.formatMessage({ id: 'nav.modal-about-change-language-saved-changes' })}</b></p>
        </div>
      </div>
    );
  };


  /* set language */
  // if user.language is null, update the value according to locale
  // if user.language is not null and the value is different from locale, update locale according to user.language
  useEffect(() => {
    if (currentUser?.language && currentUser?.language !== locale) {
      setLocale(currentUser.language);
    } else if (currentUser?.id && !currentUser?.language) {
      updateUserLanguage({
        variables: { id: currentUser.id, language: locale }
      });
    }
  }, [setLocale, updateUserLanguage, currentUser, locale]);

  return (
    <>
      <nav className="navbar fixed-top">
        <div className="d-flex align-items-center navbar-left">
          <NavLink
            to="#"
            location={{}}
            className="menu-button d-none d-md-block"
            onClick={menuButtonClick}
          >
            <MenuIcon />
          </NavLink>
          <NavLink
            to="#"
            location={{}}
            className="menu-button-mobile d-xs-block d-sm-block d-md-none"
            onClick={mobileMenuButtonClick}
          >
            <MobileMenuIcon />
          </NavLink>
          <a className="navbar-logo" href="/">
            <span className="logo d-none d-xs-block" />
            <span className="logo-mobile d-block d-xs-none" />
          </a>

        </div>

        {/* Icons group */}
        <div className="d-flex align-items-center flex-wrap" style={{ gap: '0.5rem', paddingLeft: '0.5rem' }}>
          {/* mapping icon */}
          <Link className="d-inline-block" to='/map' aria-label={intl.formatMessage({ id: 'mapping.mapping-tool' })}>
            <MappingToolIcon size='30' id='mappingIconNavBar' />
          </Link>

          {/* chat icon */}
          {isAuthenticated && currentUser?.isAdmin && discourseUrl && (
            <a
              href={chatUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="d-inline-block position-relative chat-icon-link"
              aria-label={intl.formatMessage({ id: 'chat' })}
            >
              <ChatOutlineIcon size='30' id='chatIconNavBar' />
              {unreadCount > 0 && (
                <span className='badge badge-top-right-unread-messages'>&nbsp;</span>
              )}
            </a>
          )}

          {isAuthenticated && !currentUser?.isAdmin && (
            <Link className="d-inline-block mx-2 position-relative chat-icon-link" to="/chat" aria-label={intl.formatMessage({ id: 'chat' })}>
              <ChatOutlineIcon size='30' id='chatIconNavBar' />
              {unreadCount > 0 &&
               <div className='badge badge-top-right-unread-messages'>&nbsp;</div>
              }
            </Link>
          )}

          {/* forum icon */}
          {isAuthenticated && currentUser?.isAdmin && discourseUrl && (
            <a
              href={discourseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="d-inline-block position-relative chat-icon-link"
              aria-label={intl.formatMessage({ id: 'forum' })}
            >
              <AccountGroup size='30' id='forumIconNavBar' />
            </a>
          )}

          {/* notifications inbox */}
          <div className="d-inline-block" style={{ verticalAlign: 'middle' }}>
            <NotificationInbox />
          </div>

          {/* language dropdown */}
          <div className="d-inline-block">
            <Dropdown>
              <Dropdown.Toggle size="sm" className="language-button">
                <TranslateIcon className='color-theme-1' size='20' />
              </Dropdown.Toggle>
              <Dropdown.Menu className="mt-1" right>
                {localeOptions.map((l) => {
                  return (
                    <Dropdown.Item active={locale === l.id} onClick={() => {
                      setAppLocaleOptions({ id: l.id, name: l.name, direction: l.direction });
                      handleChangeLocale();
                    }} key={l.id}>
                      {l.name}
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* login/profile manage dropdown */}
          <div className="user d-inline-block">
            {!isAuthenticated ? (
              <Link to="/login">
                <Button outline="true" color="success" className="text-white mr-3"
                  onClick={(e) => {
                    handleClickLogin(e);
                  }}
                >
                  <IntlMessages id="user.login-title" />
                </Button>
              </Link>
            ) : (
              <Dropdown className="dropdown-menu-right mr-2">
                <Dropdown.Toggle className="p-0" as={CustomDropdownToggle} id='topnavDropdown'>
                  <span color='black' className="name text-dark mr-2">{currentUser?.name}</span>
                  <span>
                    <img
                      className='m-0'
                      alt={intl.formatMessage({ id: 'general.profile' })}
                      src={currentUser?.avatarUrl ? currentUser.avatarUrl : '/assets/img/profiles/l-1.jpg'}
                    />
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="mt-3" right>
                  <Dropdown.Item onClick={() => handleProfile()}>
                    <IntlMessages id="menu.profile" />
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSettings()}>
                    <IntlMessages id="menu.settings" />
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleAbout()}>
                    <IntlMessages id="nav.about" />
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleContact()}>
                    <IntlMessages id="nav.contact" />
                  </Dropdown.Item>
                  <Dropdown.Item divider />
                  <Dropdown.Item onClick={handleLogout}>
                    <IntlMessages id="menu.sign-out" />
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        </div>
      </nav>
      {currentUser && (!currentUser.isVerified || showVerifyBanner) &&
        <div className={`d-flex top-sticky-notification ${(verificationEmailResent || verificationEmailResentError) && 'disabled'}`} >
          {
            !verificationEmailResent && !verificationEmailResentError
              ?
              <div className='my-auto mx-auto' onClick={() => {
                resendVerificationEmail({
                  variables: {
                    emailId: currentUser.userEmails.nodes[0].id,
                  }
                }).then(() => {
                  setVerificationEmailResent(true);
		}).catch((error) => {
                  console.error('Failed to resend verification email: ', error);
                  setVerificationEmailResentError(true);
                })

              }}>
                <IntlMessages id='user.verify-e-mail-reminder' />
              </div>
              : verificationEmailResentError
                ?
                // error messages
                <div className='my-auto mx-auto'>
                  <IntlMessages id='general.loading-error' />
                </div>
                :
                // success message
                <div className='my-auto mx-auto'>
                  <IntlMessages id='user.verify-e-mail-resent' values={{ userEmail: currentUser.userEmails.nodes[0].email }} />
                </div>
          }

        </div>
      }
      <InfoModal
        description={ChangeLanguageModalContent()}
        showInfoModal={showInfoModal}
        setShowInfoModal={setShowInfoModal}
        handleConfirm={handleConfirm}
      />
    </>
  );
};

TopNav.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
    location: PropTypes.any,
  }),
};

export default TopNav;
